import React, { useState } from 'react';
import { Send, MapPin, Package, CheckCircle, AlertCircle, XCircle } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { API_BASE_URL } from '../config/apiConfig';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const BookPickupPage = () => {
  const { addToast } = useToast();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isLoading, setIsLoading] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null); // ✅ Error state for UI
  const [formData, setFormData] = useState({
    customerName: '',
    email: '',
    phone: '',
    pickupAddress: '',
    destinationCountry: '',
    destinationAddress: '',
    weight: '',
    description: '',
    pickupDate: '',
  });

  const destinationCountries = [
    'United States',
    'Canada',
    'United Kingdom',
    'Germany',
    'France',
    'Nigeria',
    'Other',
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setErrorMessage(null); // ✅ Clear error when user types
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(null); // ✅ Clear previous errors

    if (!formData.customerName || !formData.email || !formData.pickupAddress || !formData.destinationCountry || !formData.destinationAddress) {
      setErrorMessage('Please fill all required fields.');
      addToast('⚠️ Please fill all required fields', 'error');
      return;
    }

    setIsLoading(true);

    try {
      const recaptchaToken = await executeRecaptcha('book_pickup');

      console.log('📦 Submitting booking:', formData);
      console.log('🌐 API URL:', `${API_BASE_URL}/shipments`);
      
      const response = await fetch(`${API_BASE_URL}/shipments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          recaptchaToken,
        }),
      });

      // ✅ Handle successful response (201 Created)
      if (response.status === 201) {
        const data = await response.json();
        console.log('✅ Booking successful:', data);

        setSuccessData({
          trackingId: data.trackingId,
          email: formData.email,
          customerName: formData.customerName,
        });

        addToast('✅ Pickup booked successfully!', 'success');
        return;
      }

      // ✅ Handle duplicate order (409 Conflict)
      if (response.status === 409) {
        setErrorMessage('This order may already exist. Please check your email for confirmation.');
        addToast('⚠️ This order may already exist.', 'warning');
        return;
      }

      // ✅ Handle validation errors (400) with specific message
      if (response.status === 400) {
        try {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const errorData = await response.json();
            if (errorData.message) {
              setErrorMessage(errorData.message);
              addToast(`❌ ${errorData.message}`, 'error');
              return;
            }
          }
        } catch (parseError) {
          console.error('❌ Could not parse error response:', parseError);
        }
      }

      // ✅ Handle all other server errors (403, 500, etc.)
      console.error('❌ Server error:', response.status);
      setErrorMessage('Booking failed. Please try again later.');
      addToast('❌ Booking failed. Please try again later.', 'error');

    } catch (error) {
      // ✅ Network errors, connection refused, timeouts
      console.error('❌ Booking error:', error);
      setErrorMessage('Unable to connect to the server. Please check your internet connection and try again.');
      addToast('❌ Booking failed. Please try again later.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSuccessData(null);
    setErrorMessage(null);
    setFormData({
      customerName: '',
      email: '',
      phone: '',
      pickupAddress: '',
      destinationCountry: '',
      destinationAddress: '',
      weight: '',
      description: '',
      pickupDate: '',
    });
  };

  // ✅ Success Page
  if (successData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4 flex items-center justify-center">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-lg shadow-2xl p-8 text-center">
            {/* Success Icon */}
            <div className="mb-6">
              <CheckCircle className="h-16 w-16 text-green-600 mx-auto" />
            </div>

            {/* Title */}
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Booking Confirmed!</h1>
            <p className="text-slate-600 mb-6">Your pickup request has been successfully submitted.</p>

            {/* Tracking ID */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 mb-6">
              <p className="text-sm text-slate-600 mb-2">Your Tracking ID:</p>
              <p className="text-2xl font-bold text-blue-600 break-all">{successData.trackingId}</p>
              <p className="text-xs text-slate-600 mt-3">Save this ID to track your shipment</p>
            </div>

            {/* Details */}
            <div className="space-y-3 text-left mb-6 bg-slate-50 p-4 rounded-lg">
              <div>
                <p className="text-xs font-semibold text-slate-600">Customer Name</p>
                <p className="text-slate-900">{successData.customerName}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-600">Email</p>
                <p className="text-slate-900">{successData.email}</p>
              </div>
            </div>

            {/* Message */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-green-900 text-sm">
                📧 A confirmation email with your tracking ID has been sent to <strong>{successData.email}</strong>
              </p>
            </div>

            {/* Next Steps */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-blue-900 text-sm">
                🚚 Next steps: Our team will contact you shortly to schedule the pickup.
              </p>
            </div>

            {/* Buttons */}
            <div className="space-y-3">
              <a
                href="/track"
                className="block w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg transition cursor-pointer text-center"
              >
                🔍 Track Shipment
              </a>
              <button
                onClick={handleReset}
                className="w-full bg-gray-200 hover:bg-gray-300 text-slate-900 font-bold py-3 px-4 rounded-lg transition cursor-pointer"
              >
                📦 Book Another Pickup
              </button>
              <a
                href="/"
                className="block w-full bg-slate-600 hover:bg-slate-700 text-white font-bold py-3 px-4 rounded-lg transition cursor-pointer text-center"
              >
                🏠 Back to Home
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ✅ Booking Form
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">📦 Book a Pickup</h1>
          <p className="text-slate-300 text-lg">Schedule your shipment with us today</p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-lg shadow-2xl p-8">
          
          {/* ✅ ERROR BANNER - Visible to user */}
          {errorMessage && (
            <div className="mb-6 p-4 bg-red-50 border-2 border-red-300 rounded-lg flex items-start gap-3 animate-pulse">
              <XCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-red-800 font-bold text-lg">Booking Failed</p>
                <p className="text-red-700">{errorMessage}</p>
              </div>
              <button 
                onClick={() => setErrorMessage(null)}
                className="text-red-600 hover:text-red-800 p-1"
                aria-label="Dismiss error"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Customer Name */}
            <div>
              <label className="block text-sm font-semibold text-slate-900 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                name="customerName"
                value={formData.customerName}
                onChange={handleInputChange}
                placeholder="John Doe"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                required
              />
            </div>

            {/* Email & Phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="john@example.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+1 234 567 8900"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                />
              </div>
            </div>

            {/* Pickup Address */}
            <div>
              <label className="block text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <MapPin className="h-4 w-4" /> Pickup Address *
              </label>
              <input
                type="text"
                name="pickupAddress"
                value={formData.pickupAddress}
                onChange={handleInputChange}
                placeholder="123 Main Street, New York, NY 10001"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                required
              />
            </div>

            {/* Destination Country */}
            <div>
              <label className="block text-sm font-semibold text-slate-900 mb-2">
                Destination Country *
              </label>
              <select
                name="destinationCountry"
                value={formData.destinationCountry}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                required
              >
                <option value="">-- Select a country --</option>
                {destinationCountries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>

            {/* Destination Address */}
            <div>
              <label className="block text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <MapPin className="h-4 w-4" /> Destination Address *
              </label>
              <input
                type="text"
                name="destinationAddress"
                value={formData.destinationAddress}
                onChange={handleInputChange}
                placeholder="456 Oak Avenue, London, UK"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                required
              />
            </div>

            {/* Weight & Pickup Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <Package className="h-4 w-4" /> Weight (kg)
                </label>
                <input
                  type="number"
                  name="weight"
                  value={formData.weight}
                  onChange={handleInputChange}
                  placeholder="5.5"
                  step="0.1"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-900 mb-2">
                  Pickup Date
                </label>
                <input
                  type="date"
                  name="pickupDate"
                  value={formData.pickupDate}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-semibold text-slate-900 mb-2">
                Package Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="What are you shipping? (e.g., Electronics, Documents, Clothing)"
                rows="4"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition resize-none"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold py-3 px-4 rounded-lg transition flex items-center justify-center gap-2 cursor-pointer"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Booking...
                </>
              ) : (
                <>
                  <Send className="h-5 w-5" /> Book Pickup
                </>
              )}
            </button>
          </form>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-8 text-center">
          <p className="text-blue-900">
            ✅ <strong>Confirmation</strong> will be sent to your email after booking.
          </p>
          <p className="text-blue-800 text-sm mt-2">
            You'll receive a tracking ID to monitor your shipment.
          </p>
        </div>
      </div>
    </div>
  );
};

export default BookPickupPage;